import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { RefreshCw, Loader2, Bot, Shield, MessageCircle, X, Send } from 'lucide-react';

const ChatLivePanel = ({ rooms, loading, onRefresh, selectedChatId, onSelectChat, onBackToList, messages, input, onInputChange, onSend, userTypingChatId, search, setSearch, notifPermission, onRequestNotif }) => {
  const bottomRef = React.useRef(null);
  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const senderBg = (t) =>
    t === 'user' ? 'bg-gray-700' : t === 'admin' ? 'bg-red-600' : 'bg-blue-600';
  const senderLabel = (t) =>
    t === 'user' ? 'UTENTE' : t === 'admin' ? 'ADMIN' : 'BOT';

  const q = (search || '').trim().toLowerCase();
  const filteredRooms = q
    ? rooms.filter(r =>
        (r.chat_id || '').toLowerCase().includes(q) ||
        (r.last_sender_name || '').toLowerCase().includes(q) ||
        (r.last_message || '').toLowerCase().includes(q)
      )
    : rooms;

  const showSidebar = !selectedChatId;
  const showChat = !!selectedChatId;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: '70vh' }}>
      {/* Sidebar */}
      <div className={`${showSidebar ? 'block' : 'hidden'} lg:block lg:col-span-1 rounded-lg border border-gray-800 overflow-hidden flex flex-col`} style={{ backgroundColor: 'var(--tp-dark)' }}>
        <div className="p-3 border-b border-gray-800">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div>
              <h3 className="text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                CHAT ATTIVE
              </h3>
              <p className="text-gray-500 text-xs">{filteredRooms.length}/{rooms.length} · {rooms.reduce((s, r) => s + (r.unread_count || 0), 0)} da leggere</p>
            </div>
            <Button onClick={onRefresh} size="sm" variant="outline" className="border-gray-700 text-white hover:bg-gray-800 h-8">
              <RefreshCw size={12} />
            </Button>
          </div>
          <Input
            data-testid="chat-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Cerca per nome, chat_id, messaggio..."
            className="bg-gray-900 border-gray-700 text-white text-xs h-8"
          />
          {notifPermission !== 'granted' && typeof Notification !== 'undefined' && (
            <button
              data-testid="enable-notif-btn"
              onClick={onRequestNotif}
              className="mt-2 w-full text-[11px] text-blue-400 hover:text-blue-300 border border-blue-800 rounded py-1 bg-blue-950/40"
            >
              🔔 Attiva notifiche popup desktop
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1" style={{ maxHeight: '60vh' }}>
          {loading && rooms.length === 0 ? (
            <div className="text-center py-10"><Loader2 size={24} className="animate-spin text-red-500 mx-auto" /></div>
          ) : filteredRooms.length === 0 ? (
            <div className="text-center py-10 px-4 text-gray-500 text-xs">
              {q ? 'Nessuna chat corrisponde alla ricerca.' : 'Nessuna chat ancora.'}
            </div>
          ) : filteredRooms.map(r => (
            <button
              key={r.chat_id}
              data-testid={`chat-room-${r.chat_id}`}
              onClick={() => onSelectChat(r.chat_id)}
              className={`w-full text-left p-3 border-b border-gray-800 hover:bg-gray-900 transition-colors ${selectedChatId === r.chat_id ? 'bg-gray-900' : ''}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-white text-xs font-bold truncate" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {r.last_sender_name || 'Utente'}
                  </span>
                  {r.unread_count > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex-shrink-0">
                      {r.unread_count}
                    </span>
                  )}
                  {userTypingChatId === r.chat_id && (
                    <span className="text-green-400 text-[9px] font-bold animate-pulse flex-shrink-0">sta scrivendo…</span>
                  )}
                </div>
                <span className="text-gray-600 text-[10px] flex-shrink-0 ml-1">
                  {r.last_timestamp ? new Date(r.last_timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : ''}
                </span>
              </div>
              <p className="text-gray-400 text-xs truncate">
                {r.last_sender_type === 'bot' && <Bot size={10} className="inline mr-1 text-blue-400" />}
                {r.last_sender_type === 'admin' && <Shield size={10} className="inline mr-1 text-red-400" />}
                {r.last_message}
              </p>
              <p className="text-gray-600 text-[10px] mt-1">{r.total_messages} msg · <code className="text-[9px]">{r.chat_id.slice(0, 12)}</code></p>
            </button>
          ))}
        </div>
      </div>

      {/* Pannello messaggi */}
      <div className={`${showChat ? 'flex' : 'hidden'} lg:flex lg:col-span-2 rounded-lg border border-gray-800 flex-col`} style={{ backgroundColor: 'var(--tp-dark)', maxHeight: '70vh' }}>
        {!selectedChatId ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm p-10 text-center">
            <div>
              <MessageCircle size={36} className="mx-auto mb-3 text-gray-700" />
              <p>Seleziona una chat a sinistra per rispondere</p>
            </div>
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-gray-800 flex items-center justify-between gap-2">
              <button
                data-testid="chat-back-btn"
                onClick={onBackToList}
                className="lg:hidden text-gray-400 hover:text-white text-xs flex items-center gap-1"
              >
                <X size={14} /> Indietro
              </button>
              <div className="flex-1 min-w-0">
                <h4 className="text-white text-sm font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>CHAT</h4>
                <code className="text-gray-500 text-[10px] truncate block">{selectedChatId}</code>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.length === 0 ? (
                <p className="text-center text-gray-500 text-xs py-8">Nessun messaggio</p>
              ) : messages.map(m => (
                <div key={m.id} className={`flex ${m.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-lg p-2.5 ${senderBg(m.sender_type)} text-white`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-bold opacity-80" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        {senderLabel(m.sender_type)} · {m.sender_name}
                      </span>
                    </div>
                    <p className="text-xs break-words whitespace-pre-wrap">{m.message}</p>
                    <p className="text-[9px] opacity-60 mt-1">
                      {m.timestamp ? new Date(m.timestamp).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>
              ))}
              {userTypingChatId === selectedChatId && (
                <div className="flex justify-start" data-testid="admin-typing-indicator">
                  <div className="bg-gray-700 text-white rounded-lg p-3 flex items-center gap-1">
                    <span className="typing-dot" />
                    <span className="typing-dot typing-dot-2" />
                    <span className="typing-dot typing-dot-3" />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
            <form
              onSubmit={e => { e.preventDefault(); onSend(); }}
              className="p-3 border-t border-gray-800 flex gap-2"
            >
              <Input
                data-testid="admin-chat-input"
                value={input}
                onChange={onInputChange}
                placeholder="Rispondi come admin..."
                className="bg-gray-900 border-gray-700 text-white text-sm flex-1"
              />
              <Button
                type="submit"
                data-testid="admin-chat-send"
                disabled={!input.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Send size={14} />
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatLivePanel;
